import { useEffect } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { trackSearchPerformed } from '@/lib/analytics-events'
import { api, buildQueryString } from '@/lib/api'
import type {
  StoryAnalytics,
  StoryDetailShape,
  StoryFilters,
  StoryListResponse,
  StorySearchResponse,
  StorySourcesResponse,
  TimeRange,
} from '@/types/story'

/**
 * Stable query-key registry for story-related queries.
 *
 * Pages and invalidation hooks should prefer these helpers over inlining
 * tuples so `queryClient.invalidateQueries({ queryKey: storyQueryKeys.all })`
 * reliably nukes every cached story read in one call.
 */
/**
 * KAN-822 T9 — search's result-type filter (`distribution`), for one request
 * only. Absent means inclusive: every result, Vendor ones labelled.
 */
export type SearchDistribution = 'mainstream' | 'vendor'

export const storyQueryKeys = {
  all: ['stories'] as const,
  list: (filters: StoryFilters) => ['stories', 'list', filters] as const,
  detail: (id: string) => ['stories', 'detail', id] as const,
  sources: (id: string) => ['stories', 'sources', id] as const,
  // KAN-822 T9 — each search result-type filter has its own entry.
  search: (q: string, distribution?: SearchDistribution) =>
    ['stories', 'search', q, distribution ?? 'all'] as const,
  analytics: (range: TimeRange) => ['stories', 'analytics', range] as const,
}

/**
 * Fetch a cursor-paginated list of story clusters from the public read API.
 *
 * Wraps `GET /stories` (session-18 T12 / PR #168). Filters are serialised via
 * `buildQueryString` — `undefined`, `null`, and `''` values are skipped, so
 * callers can pass a flat options object without pre-cleaning.
 */
export function useStories(
  filters: StoryFilters = {},
): UseQueryResult<StoryListResponse> {
  return useQuery({
    queryKey: storyQueryKeys.list(filters),
    queryFn: () => {
      // Sovereign-CERT v1: the country filter is multi-select on the client
      // but the API contract expects a single comma-joined string
      // (`?country=GB,US`). Drop the array key and pass the joined string.
      const { country, ...rest } = filters
      const serialised: Record<string, string | number | boolean | undefined> = {
        ...rest,
      }
      if (country && country.length > 0) {
        serialised.country = country.join(',')
      }
      return api.get<StoryListResponse>(
        `/stories${buildQueryString(serialised)}`,
      )
    },
    staleTime: 30_000,
  })
}

/**
 * Fetch a single story cluster by id.
 *
 * Wraps `GET /stories/:id`. Stays idle while `id` is empty so pages can call
 * this hook unconditionally from a route that may not have a valid id yet
 * (e.g. the URL param hasn't resolved, or a detail drawer is closed).
 */
export function useStory(id: string): UseQueryResult<StoryDetailShape> {
  return useQuery({
    queryKey: storyQueryKeys.detail(id),
    queryFn: () => api.get<StoryDetailShape>(`/stories/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

/**
 * Fetch the member sources attached to a story.
 *
 * Wraps `GET /stories/:id/sources`. Stays idle while `id` is empty.
 */
export function useStorySources(
  id: string,
): UseQueryResult<StorySourcesResponse> {
  return useQuery({
    queryKey: storyQueryKeys.sources(id),
    queryFn: () => api.get<StorySourcesResponse>(`/stories/${id}/sources`),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

/**
 * Full-text story search.
 *
 * Wraps `GET /stories/search?q=...`. Stays idle while `q` is empty or
 * whitespace-only so debounced search inputs don't fire a pointless request
 * on mount or between keystrokes. Lower staleTime than list/detail since the
 * command-palette and search UIs expect fresh hits.
 */
export function useStorySearch(
  q: string,
  distribution?: SearchDistribution,
): UseQueryResult<StorySearchResponse> {
  const trimmed = q.trim()
  const query = useQuery({
    queryKey: storyQueryKeys.search(trimmed, distribution),
    queryFn: () =>
      api.get<StorySearchResponse>(
        `/stories/search${buildQueryString({ q: trimmed, distribution })}`,
      ),
    enabled: Boolean(trimmed),
    staleTime: 10_000,
  })

  // KAN-32 search:performed — bound to the RESOLVED result set, not to
  // keystrokes: results_count is only knowable once data arrives, and the
  // query is already debounced by `enabled` + staleTime. Only the bucketed
  // length of the term is sent, never the term itself (R6, R22).
  const data = query.data
  useEffect(() => {
    if (!data) return
    trackSearchPerformed(trimmed, data.stories.length)
  }, [data])

  return query
}

/**
 * Fetch aggregate story-coverage analytics for a time range.
 *
 * Wraps `GET /stories/analytics?range=…` (SP4 PR-0). Backed by the
 * `storyQueryKeys.analytics` key so WS `story:created` / `story:updated`
 * events that invalidate `storyQueryKeys.all` refresh this query too.
 *
 * `staleTime` is 60s; `refetchOnWindowFocus` is disabled to avoid charts
 * snapping on tab-in.
 */
export function useStoryAnalytics(
  range: TimeRange,
): UseQueryResult<StoryAnalytics> {
  return useQuery({
    queryKey: storyQueryKeys.analytics(range),
    queryFn: () => api.get<StoryAnalytics>(`/stories/analytics?range=${range}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}
